import { useEffect, useState } from 'react';
import { Navigate, useLocation, useNavigate } from 'react-router-dom';
import { Skeleton } from '@/components/ui/skeleton';
import { ApiError } from '@/lib/api-error';
import {
  type AuthenticatedPayload,
  type TwoFactorSecret,
  confirmRequiredSetup,
  startRequiredSetup,
} from '../api/auth';
import { AuthCard } from '../components/auth-card';
import { TrustDeviceField } from '../components/trust-device-field';
import { RecoveryCodes, TwoFactorEnrollment } from '../components/two-factor-enrollment';
import { useAuth } from '../use-auth';

interface ChallengeState {
  challengeToken?: string;
  trustDeviceDays?: number;
  from?: string;
}

/** Configuração obrigatória do 2FA para administradores, antes da primeira sessão. */
export function TwoFactorSetupPage() {
  const navigate = useNavigate();
  const { applySession } = useAuth();
  const state = (useLocation().state ?? {}) as ChallengeState;
  const [secret, setSecret] = useState<TwoFactorSecret | null>(null);
  const [done, setDone] = useState<(AuthenticatedPayload & { recoveryCodes: string[] }) | null>(
    null,
  );
  const [error, setError] = useState<string | null>(null);
  const [trustDevice, setTrustDevice] = useState(false);

  useEffect(() => {
    if (!state.challengeToken) return;
    startRequiredSetup(state.challengeToken)
      .then(setSecret)
      .catch((caught: unknown) =>
        setError(
          caught instanceof ApiError ? caught.message : 'Não foi possível iniciar a configuração.',
        ),
      );
  }, [state.challengeToken]);

  if (!state.challengeToken) return <Navigate to="/entrar" replace />;
  const challengeToken = state.challengeToken;

  if (done) {
    return (
      <AuthCard title="Códigos de recuperação">
        <RecoveryCodes
          codes={done.recoveryCodes}
          onDone={() => {
            applySession(done);
            navigate(state.from ?? '/', { replace: true });
          }}
        />
      </AuthCard>
    );
  }

  return (
    <AuthCard
      title="Ative a verificação em duas etapas"
      description="Administradores precisam do segundo fator para entrar. Leva menos de um minuto."
    >
      {error ? <p className="text-sm text-destructive">{error}</p> : null}
      {secret ? (
        <div className="space-y-4">
          <TwoFactorEnrollment
            secret={secret}
            onConfirm={async (code) =>
              setDone(await confirmRequiredSetup(challengeToken, code, trustDevice))
            }
          />
          <TrustDeviceField
            days={state.trustDeviceDays}
            checked={trustDevice}
            onCheckedChange={setTrustDevice}
          />
        </div>
      ) : (
        !error && <Skeleton className="h-64 w-full" />
      )}
    </AuthCard>
  );
}
